"use client";

import type Country from "@interfaces/Country";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Flex } from "@ui/components/layout/flex/Flex";
import {
    SelectDropdown,
    SelectDropdownButton,
    SelectDropdownItem,
    SelectDropdownList,
} from "@ui/components/overlay/select-dropdown/SelectDropdown";
import { useBreakpointValue } from "@ui/hooks/useBreakpointValue";
import { dropdownButtonStyle, linkStyle, selectDropdownListStyle } from "./styles";
import { getPathname, usePathname } from "i18n/routing";
import CountryFlagWithName from "./CountryFlagWithName";
import { Box } from "@ui/components/layout/box/Box";
import { useParams } from "next/navigation";
import { LinkWrapper } from "@ui/components/navigation/link/LinkWrapper";

interface CountrySelectorProps {
    locationList: Country[];
    currentLocationId: string;
}

export const CountrySelector = ({ locationList, currentLocationId }: CountrySelectorProps) => {
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.shared, [
            CommonCMS.openCountrySelector,
            CommonCMS.countrySelector,
        ]);
    })();

    const getAriaLabelByName = (name: string) => {
        return getMicrocopy(CommonCMS.shared, CommonCMS.countrySelectorItem, {
            replacements: {
                countryName: name,
            },
        });
    };

    const currentPathname = usePathname();
    const currentParams = useParams();

    // For some reason pathname can be number or string. Our pathname is a string.
    const getLocalisedPathname = (locale) => {
        return getPathname({
            locale: locale,
            // @ts-ignore:
            href: { pathname: currentPathname.toString(), params: currentParams },
        });
    };

    const currentLocation = locationList.find(({ id }) => id === currentLocationId);
    const isMobile = useBreakpointValue({ base: true, sm: false });
    return (
        <Flex justifyContent="flex-end" alignItems="center" zIndex={2}>
            <SelectDropdown
                variant="filled"
                eventListeners={{
                    scroll: false,
                }}
                placement={isMobile ? "top-start" : "top-end"}
            >
                {({ isOpen }) => (
                    <>
                        <SelectDropdownButton
                            sx={dropdownButtonStyle}
                            aria-label={microcopies[CommonCMS.openCountrySelector]}
                        >
                            <CountryFlagWithName
                                icon={currentLocation?.icon}
                                name={currentLocation?.name}
                            />
                        </SelectDropdownButton>
                        {isOpen && (
                            <SelectDropdownList
                                aria-label={microcopies[CommonCMS.countrySelector]}
                                p={4}
                            >
                                <Box sx={selectDropdownListStyle} role="menu">
                                    {locationList.map(({ id, name, icon, link }) => {
                                        const isCurrentDomain = !!link.includes(
                                            process.env.NEXT_PUBLIC_HOSTNAME
                                        );
                                        return (
                                            <SelectDropdownItem
                                                value={id}
                                                key={id}
                                                px={0}
                                                as={LinkWrapper}
                                                href={
                                                    isCurrentDomain
                                                        ? getLocalisedPathname(id)
                                                        : link
                                                }
                                                // locale={id}
                                                rel={isCurrentDomain ? "nofollow" : undefined}
                                                aria-label={getAriaLabelByName(name)}
                                                sx={linkStyle}
                                            >
                                                <CountryFlagWithName icon={icon} name={name} />
                                            </SelectDropdownItem>
                                        );
                                    })}
                                </Box>
                            </SelectDropdownList>
                        )}
                    </>
                )}
            </SelectDropdown>
        </Flex>
    );
};
